/**
 * InlineCitationCard — the card that appears RIGHT IN the assistant
 * message body, immediately after a paragraph that cited it.
 *
 * Sprint AI-MODULE v2.1 follow-up. The model writes "…the file is
 * **2026-IT-Eng-Budgeting** [1]…" and the user shouldn't have to
 * scroll to a separate sources section to see what [1] is. This
 * component is rendered by AssistantText for every cw_file /
 * dm_document citation referenced inline.
 *
 * The preview goes through the SAME server-side proxy Connected
 * Search uses (``/api/v1/mod/cw/files/{vortex_id}/thumbnail``).
 * That endpoint resolves three shapes:
 *
 *   - ``application/json`` + ``{ embed_url }``  → Box iframe URL
 *   - ``text/html``                              → Atlassian HTML, wrapped in blob
 *   - ``image/*``                                → image bytes → blob URL
 *
 * Provider X-Frame-Options (Drive's SAMEORIGIN, etc.) is bypassed
 * because the iframe/img URL is now from our own origin.
 */
import { useEffect, useState, type FC } from 'react';
import {
  CheckCircle2,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Presentation,
  Image as ImageIcon,
  File as FileIcon,
  Lock,
} from 'lucide-react';
import type { CitationRef } from '../api/aiApi';
import { useAuthStore } from '@/stores/authStore';

interface Props {
  citation: CitationRef;
}

interface PreviewState {
  loading: boolean;
  blobUrl?: string;
  embedUrl?: string;
  error?: string;
}

const InlineCitationCard: FC<Props> = ({ citation }) => {
  const isDmDoc = citation.kind === 'dm_document';
  const isFile = citation.kind === 'cw_file';
  const isExternal = /^https?:\/\//i.test(citation.url);

  const [preview, setPreview] = useState<PreviewState>({ loading: false });
  const accessToken = useAuthStore((s) => s.accessToken);

  // Fetch the proxied thumbnail/preview on mount when we have a
  // vortex_id (CW-cache-backed file).
  useEffect(() => {
    if (!isFile || !citation.vortex_id) return;
    let cancelled = false;
    let revoke: string | null = null;
    setPreview({ loading: true });
    (async () => {
      try {
        const resp = await fetch(
          `/api/v1/mod/cw/files/${citation.vortex_id}/thumbnail`,
          { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const ct = resp.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await resp.json();
          if (cancelled) return;
          if (data.embed_url) {
            setPreview({ loading: false, embedUrl: data.embed_url });
          } else {
            setPreview({ loading: false, error: 'No preview available.' });
          }
        } else {
          const blob = await resp.blob();
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          revoke = url;
          setPreview({ loading: false, blobUrl: url });
        }
      } catch (err) {
        if (cancelled) return;
        setPreview({
          loading: false,
          error: err instanceof Error ? err.message : 'Preview unavailable.',
        });
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isFile, citation.vortex_id, accessToken]);

  const isHtmlBlob = preview.blobUrl?.startsWith('blob:') ?? false;

  return (
    <div className="ai-inline-card">
      <a
        className="ai-inline-card__header"
        href={citation.url}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        <span className="ai-inline-card__icon">
          {isDmDoc ? <Lock size={18} /> : mimeIcon(citation.mime_type, 18)}
        </span>
        <span className="ai-inline-card__title-block">
          <span className="ai-inline-card__title">{citation.label}</span>
          <span className="ai-inline-card__sub">
            {isDmDoc ? 'Document Management' : (citation.provider ?? 'Source')}
            {citation.parent_name && ` · ${citation.parent_name}`}
            {citation.last_modified && ` · ${formatRelative(citation.last_modified)}`}
          </span>
        </span>
        <ExternalLink size={14} className="ai-inline-card__out" />
      </a>

      {/* Preview body — only rendered for cw_file when the thumbnail
          proxy returned something. We don't try to embed providers
          ourselves: the proxy already resolved the right shape. */}
      {isFile && preview.embedUrl && (
        <iframe
          className="ai-inline-card__preview"
          src={preview.embedUrl}
          title={`Preview of ${citation.label}`}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          loading="lazy"
        />
      )}
      {isFile && preview.blobUrl && !isHtmlBlob && (
        // image/* — render directly.
        <img
          className="ai-inline-card__thumb"
          src={preview.blobUrl}
          alt={`Preview of ${citation.label}`}
          referrerPolicy="no-referrer"
        />
      )}
      {isFile && preview.blobUrl && isHtmlBlob && (
        // text/html — blob URL served from our origin, safe to iframe.
        <iframe
          className="ai-inline-card__preview"
          src={preview.blobUrl}
          title={`Preview of ${citation.label}`}
          sandbox="allow-same-origin"
          loading="lazy"
        />
      )}
      {isFile && preview.loading && (
        <div className="ai-inline-card__preview-empty">Loading preview…</div>
      )}

      <div className="ai-inline-card__meta">
        {citation.owner_name && (
          <span className="ai-inline-card__chip">Owner · {citation.owner_name}</span>
        )}
        {citation.mime_type && (
          <span className="ai-inline-card__chip">{prettyMime(citation.mime_type)}</span>
        )}
        {citation.file_size_bytes != null && (
          <span className="ai-inline-card__chip">{formatBytes(citation.file_size_bytes)}</span>
        )}
        {isDmDoc && (
          <a className="btn btn--sm btn--primary ai-inline-card__cta" href={citation.url}>
            <CheckCircle2 size={12} /> Acknowledge now
          </a>
        )}
      </div>
    </div>
  );
};

export default InlineCitationCard;


// ── helpers ─────────────────────────────────────────────────────────

function mimeIcon(mime: string | undefined, size: number) {
  if (!mime) return <FileText size={size} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return <FileSpreadsheet size={size} />;
  if (mime.includes('presentation') || mime.includes('powerpoint')) return <Presentation size={size} />;
  if (mime.startsWith('image/')) return <ImageIcon size={size} />;
  if (mime.includes('document') || mime.includes('word') || mime === 'text/plain' || mime === 'application/pdf') return <FileText size={size} />;
  return <FileIcon size={size} />;
}

function prettyMime(mime: string): string {
  const map: Record<string, string> = {
    'application/vnd.google-apps.spreadsheet': 'Google Sheet',
    'application/vnd.google-apps.document': 'Google Doc',
    'application/vnd.google-apps.presentation': 'Google Slides',
    'application/vnd.google-apps.folder': 'Google Drive folder',
    'application/pdf': 'PDF',
    'text/plain': 'Text',
    'text/csv': 'CSV',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel workbook',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  };
  return map[mime] ?? mime;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatRelative(isoTs: string): string {
  const t = new Date(isoTs);
  if (Number.isNaN(t.getTime())) return isoTs;
  const diffMs = Date.now() - t.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < day) return 'today';
  if (diffMs < 2 * day) return 'yesterday';
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)} days ago`;
  if (diffMs < 30 * day) return `${Math.floor(diffMs / (7 * day))} weeks ago`;
  return t.toISOString().slice(0, 10);
}
